import React, { FC, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  FieldsRow,
  FieldsSection,
  FormCheckbox,
  FormNumericField,
  FormTextField,
} from '../../../../../shared/components/form';
import { useTranslation } from 'react-i18next';
import { C_Country } from '../../../../../graphql/generatedModel.ts';
import { configurationTabState } from '../../states/configuration.state.ts';
import { TCompanyConfigs } from '../../../../../shared/services/companyConfig/companyConfig.service.ts';

export const FinancialAccountingTab: FC<IFinancialAccountingTab> = ({ params }) => {
  const { countryId, ...defaultValues } = params;
  const isDatev = useMemo(
    () => countryId === C_Country.CO2_GERMANY || countryId === C_Country.CO3_AUSTRIA,
    [countryId],
  );

  const { t } = useTranslation();
  const formMethods = useForm({ defaultValues, mode: 'onChange' });
  const {
    resetField,
    formState: { dirtyFields },
  } = formMethods;
  useEffect(() => {
    const formStateSub = formMethods.watch((data: Record<string, any>, reason) => {
      if (reason.type === 'change' && reason.name) {
        const fieldName = reason.name;
        configurationTabState.pub.recordData({ [fieldName]: data[fieldName] });
      }
    });
    const configurationTabStateSub = configurationTabState.sub
      .state()
      .subscribe(({ dataToSave }) => {
        const name = Object.keys(dataToSave)[0] as keyof typeof dataToSave;
        resetField(name, {
          defaultValue: dataToSave[name],
          keepDirty: false,
        });
      });

    return () => {
      formStateSub.unsubscribe();
      configurationTabStateSub.unsubscribe();
    };
  }, []);

  const onSubmit = (e: React.FocusEvent<HTMLFormElement>) => {
    if (!!dirtyFields[e.target.name as keyof typeof dirtyFields]) {
      configurationTabState.pub.saveData();
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form noValidate onBlur={onSubmit}>
        <FieldsSection
          titleBoxSx={{ pt: 0 }}
          nomt
          title={t(`settings.${isDatev ? 'datev' : 'abacus'}`)}
        >
          <FieldsRow>
            {!isDatev ? (
              <FormNumericField
                name='fibuClient'
                precision={0}
                label={t('settings.client')}
                shrink
              />
            ) : (
              <FormCheckbox name='isDatevCommitment' label={t(`settings.enable_datev_export`)} />
            )}
          </FieldsRow>
          <FieldsRow>
            <FormTextField
              slotProps={{ htmlInput: { maxLength: 20 } }}
              name='fibuVatCodeNormal'
              label={t('settings.vat_normal')}
              shrink
            />
            <FormTextField
              slotProps={{ htmlInput: { maxLength: 20 } }}
              name='fibuVatCodeReduced'
              label={t('settings.vat_reduced')}
              shrink
            />
            <FormTextField
              slotProps={{ htmlInput: { maxLength: 20 } }}
              name='fibuVatCodeWithoutVAT'
              label={t('settings.no_vat')}
              shrink
            />
          </FieldsRow>
        </FieldsSection>
      </form>
    </FormProvider>
  );
};

interface IFinancialAccountingTab {
  params: TCompanyConfigs;
}
